import type {
  CdwGridColumn,
  CdwGridColumnLookup,
  CdwGridSort,
  DateTimeSettings,
  DeviceAsset,
} from '@cdw-ae/common-data';
import {
  DEVICE_DB_FIELD_PATHS,
  DeviceDiskUsed,
  formatBytes,
  Vendors,
} from '@cdw-ae/common-data';
import { formatForGrid } from '@cdw-ae/date-time-service';

export const DEVICE_SEARCH_IMPORT_EXPORT_TEMPLATE_COLUMNS_ORDER: CdwGridColumn[] =
  [
    // Identity column
    {
      name: 'id',
    },
    // Non-editable visible columns
    {
      name: 'serialNumber',
    },
    {
      name: 'model',
    },
    {
      name: 'status',
    },
    {
      name: 'lastKnownUser',
    },
    // Editable columns group (visible by default)
    {
      name: 'orgUnitPath',
    },
    {
      name: 'annotatedAssetId',
    },
    {
      name: 'annotatedUser',
    },
    {
      name: 'annotatedLocation',
    },
    {
      name: 'notes',
    },
    // Hidden columns - needed in case of filter or sort on them
    {
      name: 'vendorId',
      hide: true,
    },
    {
      name: 'lastUpdated',
      hide: true,
    },
    {
      name: 'lastSync',
      hide: true,
    },
    {
      name: 'firstEnrollmentTime',
      hide: true,
    },
    {
      name: 'lastEnrollmentTime',
      hide: true,
    },
    {
      name: 'osVersion',
      hide: true,
    },
    {
      name: 'autoUpdateExpiration',
      hide: true,
    },
    {
      name: 'lastStableChromeVersion',
      hide: true,
    },
    // {
    //   name: 'maximumDomainAllowedChromeVersion',
    //   hide: true,
    // },
    {
      name: 'lastAssetSync',
      hide: true,
    },
    {
      name: 'bootTime',
      hide: true,
    },
    {
      name: 'batteryHealth',
      hide: true,
    },
    {
      name: 'diskTotal',
      hide: true,
    },
    {
      name: 'diskAvailable',
      hide: true,
    },
    {
      name: 'diskUsedCategory',
      hide: true,
    },
    {
      name: 'designCapacityMah',
      hide: true,
    },
    {
      name: 'fullChargeCapacity',
      hide: true,
    },
  ];

export const DEVICE_SEARCH_SORT_TIEBREAKER: CdwGridSort = { name: 'id', direction: 'ASC' };

export const DEVICE_SEARCH_EXPORT_NAME = 'Device(s)';

/**
 * Display string for the combined "Model" column (EDUS-2618). Shared with the
 * Audit Log Detail grid (EDUS-3135) so both views render a device identically.
 */
export function formatDeviceMakeAndModel(
  make: string | null | undefined,
  model: string | null | undefined
): string {
  const safeMake = make ?? '';
  const safeModel = model ?? '';

  // Defensive: if model already starts with make, don't duplicate
  // Handles legacy seeded data where model contained the full string
  if (safeMake && safeModel && safeModel.startsWith(`${safeMake} `)) {
    return safeModel;
  }

  return safeMake && safeModel ? `${safeMake} ${safeModel}` : safeMake || safeModel;
}

export const DEVICE_SEARCH_COLUMN_LOOKUP: CdwGridColumnLookup = {
  // Editable columns group (visible by default)
  orgUnitPath: {
    displayName: 'OU Path',
    flex: 1,
    minWidth: 200,
    columnGroupShow: 'open',
    sqlName: DEVICE_DB_FIELD_PATHS.orgUnitPath,
  },
  // EDUS-2591: no valueFormatter on editable columns — a cleared field comes
  // back from Google as null and an "N/A" placeholder reads as a real value.
  annotatedAssetId: {
    displayName: 'Asset ID',
    flex: 1,
    minWidth: 150,
    columnGroupShow: 'open',
    sqlName: DEVICE_DB_FIELD_PATHS.annotatedAssetId,
  },
  annotatedUser: {
    displayName: 'Annotated User',
    flex: 1,
    minWidth: 180,
    columnGroupShow: 'open',
    sqlName: DEVICE_DB_FIELD_PATHS.annotatedUser,
  },
  annotatedLocation: {
    displayName: 'Annotated Location',
    flex: 1,
    minWidth: 250,
    columnGroupShow: 'open',
    sqlName: DEVICE_DB_FIELD_PATHS.annotatedLocation,
  },
  notes: {
    displayName: 'Notes',
    flex: 1,
    minWidth: 250,
    columnGroupShow: 'open',
    sqlName: DEVICE_DB_FIELD_PATHS.notes,
  },
  // Non-editable visible columns
  status: {
    displayName: 'Device Status',
    flex: 1,
    minWidth: 180,
    sqlName: DEVICE_DB_FIELD_PATHS.status,
  },
  osVersion: {
    displayName: 'OS Version',
    flex: 1,
    minWidth: 180,
    sqlName: 'vendorProperties.google.osVersion',
  },
  lastKnownUser: {
    displayName: 'Recent User',
    flex: 1,
    minWidth: 200,
    sqlName: 'vendorProperties.google.lastKnownUser',
  },
  // Hidden columns (can be shown via column visibility panel)
  id: {
    displayName: 'CDW Device ID',
    flex: 1,
    minWidth: 280,
    hide: true,
    sqlName: DEVICE_DB_FIELD_PATHS.id,
  },
  vendorId: {
    displayName: 'Device ID (Vendor)',
    flex: 1,
    minWidth: 200,
    hide: true,
    sqlName: DEVICE_DB_FIELD_PATHS.vendorId,
  },
  serialNumber: {
    displayName: 'Serial Number',
    flex: 1,
    minWidth: 180,
    hide: true,
    sqlName: DEVICE_DB_FIELD_PATHS.serialNumber,
  },
  model: {
    displayName: 'Model',
    flex: 1,
    minWidth: 200,
    // EDUS-2618: visible by default. This is now the only column carrying the brand
    // (the standalone Make column was removed), so hiding it left the default grid
    // showing neither make nor model.
    hide: false,
    sqlName: DEVICE_DB_FIELD_PATHS.model,
    valueGetter: (params): string => {
      const device = (params as { data: unknown }).data as DeviceAsset | undefined;
      if (!device) return '';
      return formatDeviceMakeAndModel(device.make, device.model);
    },
  },
  lastUpdated: {
    displayName: 'OS Last Updated',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.lastUpdated',
  },
  lastSync: {
    displayName: 'Last Sync',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.lastSync',
  },
  firstEnrollmentTime: {
    displayName: 'First Enrollment Time',
    flex: 1,
    minWidth: 220,
    hide: true,
    // EDUS-2346: copy is verbatim from the ticket (Tracy's 2026-09-04
    // comment supersedes the longer wording in the description).
    headerTooltip:
      'Data provided by Google. Resets if the device is deprovisioned and re-enrolled.',
    headerIcon: 'info_outline',
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.firstEnrollmentTime',
  },
  lastEnrollmentTime: {
    displayName: 'Last Enrollment Time',
    flex: 1,
    minWidth: 220,
    hide: true,
    headerTooltip:
      'Data provided by Google. Shows the latest re-enrollment date (N/A if enrolled once).',
    headerIcon: 'info_outline',
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.lastEnrollmentTime',
  },
  autoUpdateExpiration: {
    displayName: 'Auto Update Expiration',
    flex: 1,
    minWidth: 250,
    hide: true,
    valueGetter: (params) => {
      const device = (params as { data: unknown }).data as
        | DeviceAsset
        | undefined;
      const value =
        device?.vendorProperties?.[Vendors.GOOGLE]?.autoUpdateExpiration ||
        device?.vendorProperties?.[Vendors.GOOGLE]?.autoUpdateThrough;
      if (!value) return null;
      // Convert millisecond timestamp to ISO string for Luxon compatibility
      const timestamp =
        typeof value === 'number' ? value : Number.parseInt(String(value), 10);
      if (!Number.isNaN(timestamp) && timestamp > 0) {
        return new Date(timestamp).toISOString();
      }
      return value;
    },
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.autoUpdateExpiration',
  },
  lastStableChromeVersion: {
    displayName: 'Last Stable Chrome Version',
    flex: 1,
    minWidth: 250,
    hide: true,
    sortable: false,
    sqlName: 'vendorProperties.google.lastStableChromeVersion',
  },
  // maximumDomainAllowedChromeVersion: {
  //   displayName: 'Max Domain Chrome Version',
  //   flex: 1,
  //   minWidth: 250,
  //   hide: true,
  //   sqlName: 'vendorProperties.google.maximumDomainAllowedChromeVersion',
  // },
  lastAssetSync: {
    displayName: 'Last Asset Sync',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'lastAssetSync',
  },
  bootTime: {
    displayName: 'Last Reboot Time',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueGetter: (params) => {
      const device = (params as { data: unknown }).data as
        | DeviceAsset
        | undefined;
      return device?.vendorProperties?.[Vendors.GOOGLE]?.bootTime || null;
    },
    valueFormatter: (params, currentDateTimeSettings) =>
      formatForGrid(
        (params as { value: unknown }).value as string,
        currentDateTimeSettings as DateTimeSettings
      ),
    sqlName: 'vendorProperties.google.bootTime',
  },
  batteryHealth: {
    displayName: 'Battery Health',
    flex: 1,
    minWidth: 180,
    hide: true,
    sqlName: 'vendorProperties.google.batteryHealth',
  },
  diskTotal: {
    displayName: 'Disk Total',
    flex: 1,
    minWidth: 150,
    hide: true,
    valueFormatter: (params) =>
      formatBytes((params as { value: unknown }).value as string),
    sqlName: 'vendorProperties.google.diskTotal',
  },
  diskAvailable: {
    displayName: 'Disk Available',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueFormatter: (params) =>
      formatBytes((params as { value: unknown }).value as string),
    sqlName: 'vendorProperties.google.diskAvailable',
  },
  diskUsedCategory: {
    displayName: 'Disk Used',
    flex: 1,
    minWidth: 150,
    hide: true,
    valueFormatter: (params) => {
      const category = (params as { value: unknown }).value as string;
      switch (category) {
        case DeviceDiskUsed.Low:
          return 'Low (< 50%)';
        case DeviceDiskUsed.Fifty:
          return '50%';
        case DeviceDiskUsed.Sixty:
          return '60%';
        case DeviceDiskUsed.Seventy:
          return '70%';
        case DeviceDiskUsed.Eighty:
          return '80%';
        case DeviceDiskUsed.Ninety:
          return '90%';
        default:
          return '';
      }
    },
    sqlName: 'vendorProperties.google.diskUsedCategory',
  },
  // EDUS-2835: keyed `designCapacityMah`, not `designCapacity`. The export
  // re-derives this key from the colId (= sqlName) — see the invariant test in
  // device-search.config.spec.ts.
  designCapacityMah: {
    displayName: 'Design Capacity',
    flex: 1,
    minWidth: 180,
    hide: true,
    valueGetter: (params) => {
      const device = (params as { data: unknown }).data as
        | DeviceAsset
        | undefined;
      return (
        device?.vendorProperties?.[Vendors.GOOGLE]?.designCapacityMah || null
      );
    },
    sqlName: 'vendorProperties.google.designCapacityMah',
  },
  fullChargeCapacity: {
    displayName: 'Full Charge Capacity',
    flex: 1,
    minWidth: 200,
    hide: true,
    sqlName: 'vendorProperties.google.fullChargeCapacity',
  },
};
